/**
 * Avatar stack — shows who else is currently in this project's viewer.
 *
 * Sits top-center so it doesn't collide with the top-right "Projekte /
 * Abmelden" buttons or the top-right Messungen panel.
 */

import type { PresenceMember } from '../realtime/types'
import type { WsStatus } from '../realtime/types'

interface Props {
  members: PresenceMember[]
  status: WsStatus
}

export function PresencePanel({ members, status }: Props) {
  const showStatus = status !== 'open'
  if (members.length === 0 && !showStatus) return null

  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 10px',
        background: 'rgba(20,22,26,0.85)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 999,
        boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(6px) saturate(140%)',
        WebkitBackdropFilter: 'blur(6px) saturate(140%)',
      }}
    >
      {showStatus && (
        <span
          style={{
            fontSize: 11,
            color: '#cfd2d6',
            fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
            paddingRight: members.length > 0 ? 6 : 0,
            borderRight: members.length > 0 ? '1px solid rgba(255,255,255,0.14)' : undefined,
          }}
        >
          {status === 'connecting' && 'Verbinde…'}
          {status === 'reauth' && 'Sitzung wird aktualisiert…'}
          {status === 'closed' && 'Offline'}
        </span>
      )}
      <div style={{ display: 'flex', gap: -8 }}>
        {members.map((m, i) => (
          <Avatar key={m.id} member={m} offset={i} />
        ))}
      </div>
    </div>
  )
}

function Avatar({ member, offset }: { member: PresenceMember; offset: number }) {
  const tooltip = `${member.first_name} ${member.last_name}`.trim() || member.email
  return (
    <span
      title={tooltip}
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: '#' + member.color.toString(16).padStart(6, '0'),
        color: '#fff',
        fontWeight: 700,
        fontSize: 11,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px solid rgba(20,22,26,0.85)',
        marginLeft: offset === 0 ? 0 : -8,
        textShadow: '0 0 1px rgba(0,0,0,0.5)',
        userSelect: 'none',
      }}
    >
      {member.initials || '?'}
    </span>
  )
}
